import {
  createContext, useCallback, useContext, useEffect, useId, useMemo, useState, type ReactNode,
} from 'react';
import type { ApiClient, AussenBerichtsstand, AussenUebersicht } from '../../api';
import { bereichname, bewertungname, bewertungzustand } from '../../aussenKlartext';
import { OHNE_WERT, StatusBadge, type ExportZiel, type Fachstatus, type Vorbehalt } from '../../muster';
import { rechenstandText, type Laufangabe } from '../sanitaer/kopf';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * KOPF DER AUSSENANLAGEN (Paket TS9b1b, TX-STANDARD S1.2, S1.9)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die acht Außenanlagen-Seiten sind Reiter EINER Seite (`AussenanlagenRahmen`,
 * ein `<h1>`, Entscheidung E1 wie MSR). Was in den Kopf gehört - Rechenstand,
 * Exportleiste, Vorbehalte -, kennt aber nur die Unterseite: der Bereich der
 * Berechnung, der Freispiegellauf des Nachweises, der Bericht der Leitungsliste.
 *
 * Jede Unterseite meldet deshalb über {@link useAussenKopf}, was sie zeigt; der
 * Rahmen sammelt die Meldungen ({@link useAussenKopfSammlung}) und setzt sie in
 * `PageHeader` (meta, aktionen) und in den Vorbehaltskasten unter den Sperren -
 * dasselbe Muster wie `useMsrExport` in `seiten/msr/kopf.tsx`, nur mit
 * Rechenstand und Vorbehalten. Ohne Rahmen (Einzeltest) geht die Meldung ins
 * Leere.
 */

/** Was eine Unterseite in den Kopf meldet. */
export interface AussenKopfMeldung {
  /** Der Rechenstand (S1.2) - die erste Meldung mit Rechenstand gilt. */
  meta?: ReactNode;
  /** Die Exportziele der Unterseite (S1.9), in Meldefolge. */
  ziele?: ExportZiel[];
  /** Vorbehalte der Unterseite; sie stehen im Kasten unter dem Kopf nach den Sperren. */
  vorbehalte?: Vorbehalt[];
}

/** Meldet die Kopfangaben einer Stelle (`null` = abmelden). */
export type AussenKopfMelden = (stelle: string, meldung: AussenKopfMeldung | null) => void;

export const AussenKopfKontext = createContext<AussenKopfMelden>(() => {});

/**
 * Meldet die Kopfangaben dieser Stelle, solange sie zu sehen ist. Die Meldung wird neu gesendet, wenn sich
 * `abhaengig` ändert (Bereich, Lauf, Fehler) - nicht bei jedem Zeichnen, sonst zeichnete der Rahmen endlos.
 */
export function useAussenKopf(meldung: AussenKopfMeldung, abhaengig: readonly unknown[]) {
  const melden = useContext(AussenKopfKontext);
  const stelle = useId();
  useEffect(() => {
    melden(stelle, meldung);
    return () => melden(stelle, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [melden, stelle, ...abhaengig]);
}

/** Die Kopfangaben aller Stellen: der erste Rechenstand, alle Ziele und Vorbehalte in Meldefolge. */
export function kopfZusammenfassen(meldungen: readonly AussenKopfMeldung[]): {
  meta: ReactNode; ziele: ExportZiel[]; vorbehalte: Vorbehalt[];
} {
  return {
    meta: meldungen.find((m) => m.meta !== undefined && m.meta !== null)?.meta,
    ziele: meldungen.flatMap((m) => m.ziele ?? []),
    vorbehalte: meldungen.flatMap((m) => m.vorbehalte ?? []),
  };
}

/** Die Sammelstelle des Rahmens: `melden` geht an den Kontext, `kopf` in den `PageHeader`. */
export function useAussenKopfSammlung() {
  const [stellen, setStellen] = useState<Record<string, AussenKopfMeldung>>({});
  const melden: AussenKopfMelden = useCallback((stelle, meldung) => {
    setStellen((alt) => {
      const neu = { ...alt };
      if (meldung) neu[stelle] = meldung;
      else delete neu[stelle];
      return neu;
    });
  }, []);
  const kopf = useMemo(() => kopfZusammenfassen(Object.values(stellen)), [stellen]);
  return { melden, kopf };
}

// ═══ Sperren als Vorbehalte (S1.2 Nr. 2) ═════════════════════════════════════

/**
 * Die Sperren der Übersicht im Vorbehaltskasten. Eine Sperre (`blocker`) sagt, was fehlt, damit gerechnet werden
 * DARF - kf-Wert, Einzugsfläche, Löschwasseranforderung - und das kann nur der Eigentümer beibringen: Fachfrage.
 * Eine Warnung (unbestätigte Flächen, Leitungen ohne Kreis) ist ein Vorbehalt am Ergebnis.
 */
export function sperrenVorbehalte(sperren: AussenUebersicht['sperren']): Vorbehalt[] {
  return sperren.map((s): Vorbehalt => (s.gewicht === 'blocker'
    ? { art: 'fachfrage', text: `Sperre: ${s.titel}. ${s.text}` }
    : { art: 'sonstiges', text: `${s.titel}. ${s.text}` }));
}

// ═══ Bewertung und Rechenstand ════════════════════════════════════════════════

/**
 * Die Bewertung einer Zeile als Statusmarke - drei Zustände wie auf der Berechnungsseite (TS9a): erfüllt, eine
 * Nachweislücke (`ohne_abfluss`, `nicht_pruefbar` - kein Mangel) und ein Befund.
 */
export function bewertungStatus(bewertung: string | null | undefined): Fachstatus {
  const z = bewertungzustand(bewertung);
  return {
    zustand: z === 'gut' ? 'gut' : z === 'neutral' ? 'info' : 'warnung',
    text: bewertungname(bewertung),
  };
}

/** Ein kf-Wert in Exponentenschreibweise („1,0e-5“) - `formatiereZahl` kennt keine Exponenten. */
export function kfText(kf: number | null | undefined, stellen = 1): string {
  return typeof kf === 'number' && Number.isFinite(kf) ? kf.toExponential(stellen).replace('.', ',') : OHNE_WERT;
}

/** Der Server meldet einen fehlenden Lauf als Fehler mit diesem Satz („Fuer diesen Bereich wurde noch nicht gerechnet.“). */
export function nochNichtGerechnet(meldung: string | null | undefined): boolean {
  return /noch nicht gerechnet/i.test(meldung ?? '');
}

/**
 * Der Rechenstand eines Nachweislaufs. Die Nachweisabfragen (`nachweis/straenge` …) liefern Lauf und Zeitpunkt, aber
 * keinen Modellstand - die Marke sagt das, statt „aktuell“ zu behaupten. Ob der Lauf veraltet ist, steht im Kopf
 * der Berechnungsseite (Bereich Freispiegelentwässerung).
 */
export function NachweisRechenstand({ lauf }: { lauf: Laufangabe }) {
  return (
    <span data-rechenstand>
      {rechenstandText(lauf)}{' '}
      <StatusBadge
        zustand="neutral" text="Modellstand nicht geprüft" klein
        titel="Der Nachweis liest den jüngsten Lauf. Ob er älter ist als die jüngste Außenaufnahme, zeigt die Seite Berechnungen."
      />
    </span>
  );
}

// ═══ Der Bericht eines Bereichs (S1.9) ═══════════════════════════════════════

/** Grund an einem gesperrten Ziel ohne Lauf - derselbe Satz wie an der Excel-Mappe der Berechnungsseite. */
export const OHNE_LAUF = 'Für diesen Bereich wurde noch nicht gerechnet.';

/**
 * Der PDF-Bericht eines Außenbereichs als Exportziel - mit dem, was bisher am `AussenBerichtKnopf` stand: die
 * Dokumentnummer des letzten Berichts und ob seither neu gerechnet wurde. Ohne Stand (nicht ladbar) bleibt das Ziel
 * ein Ziel; was dann fehlt, ist die Nummer, nicht die Möglichkeit.
 */
export function berichtZiel(
  api: ApiClient, projektId: string, bereich: string, stand: AussenBerichtsstand | null | undefined,
): ExportZiel {
  const nummer = stand?.dokumentnummer;
  return {
    art: 'pdf',
    beschriftung: `PDF ${bereichname(bereich)}`,
    laden: () => api.aussenBerichtPdf(projektId, bereich),
    hinweis: nummer
      ? (stand?.aktuell ? `zuletzt ${nummer}` : `zuletzt ${nummer} - seither neu gerechnet`)
      : undefined,
    gesperrt: stand && !stand.hatLauf ? { grund: OHNE_LAUF } : undefined,
  };
}

/**
 * Der Berichtsstand EINES Bereichs. `undefined` = Abruf läuft, `null` = nicht ladbar (kein Fehler der Seite: Der
 * Bericht bleibt abrufbar, nur ohne Nummer). Je Seite neu geholt, weil ein Lauf auf der Berechnungsseite jeden
 * vorhandenen Bericht überholt.
 */
export function useBerichtsstand(api: ApiClient, projektId: string, bereich: string) {
  const [stand, setStand] = useState<AussenBerichtsstand | null | undefined>(undefined);
  useEffect(() => {
    let lebt = true;
    setStand(undefined);
    api.aussenBerichte(projektId)
      .then((x) => { if (lebt) setStand(x.bereiche.find((b) => b.bereich === bereich) ?? null); })
      .catch(() => { if (lebt) setStand(null); });
    return () => { lebt = false; };
  }, [api, projektId, bereich]);
  return stand;
}
